import { FieldSchemaFormBuilderV2, SchemaFactoryV2 } from "@ucl/library";
import { buildComplexFieldKey, COMPLEX_FIELD_KEYS, FIELD_KEYS, MODEL_KEYS } from "@/constants";
import {
  EmploymentInformationComplexField,
  PersonInformationComplexField,
  DigitalBankingInformationComplexField,
  GovernmentIdInformationComplexField,
  SecurityQuestionsInformationComplexField,
  DebitCardInformationComplexField,
} from "@/components/ComplexFields";
import { FinancialApplication } from "@/types/FinancialApplication";
import { OrganizationInformationComplexField } from "@/components/ComplexFields/OrganizationInformationComplexField";
import { PersonalAccountAcceptableUseComplexField } from "@/components/ComplexFields/PersonalAccountAcceptableUseComplexField";
import { OrganizationAccountAcceptableUseComplexField } from "@/components/ComplexFields/OrganizationAccountAcceptableUseComplexField";
import { PersonalAccountRiskReviewComplexField } from "@/components/ComplexFields/PersonalAccountRiskReviewComplexField";
import { OrganizationContactInformationComplexField } from "@/components/ComplexFields/OrganizationContactInformationComplexField";
import { LawFirmOrganizationAccountRiskReviewComplexField } from "@/components/ComplexFields/LawFirmOrganizationAccountRiskReviewComplexField";
import { NonLawFirmOrganizationAccountRiskReviewComplexField } from "@/components/ComplexFields/NonLawFirmOrganizationAccountRiskReviewComplexField";
import { FinancialApplicationDocumentsComplexField } from "@/components/ComplexFields/FinancialApplicationDocumentsComplexField";
import { FinancialApplicationFormSectionContextType } from "@/hooks/useFinancialApplicationFormSectionValidation";

export const createFinancialApplicationFormBuilder = (
  schemaFactory: SchemaFactoryV2,
  executionContextType: FinancialApplicationFormSectionContextType
): FieldSchemaFormBuilderV2<FinancialApplication> => {
  return (onValueChange, form, errors) => {
    const applicantRequiredFields: string[] = [
      // Person Information Complex Fields
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.PERSON_INFORMATION,
        COMPLEX_FIELD_KEYS.PERSON_INFORMATION.FIRST_NAME
      ),
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.PERSON_INFORMATION,
        COMPLEX_FIELD_KEYS.PERSON_INFORMATION.LAST_NAME
      ),
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.PERSON_INFORMATION,
        COMPLEX_FIELD_KEYS.PERSON_INFORMATION.DATE_OF_BIRTH
      ),
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.PERSON_INFORMATION,
        COMPLEX_FIELD_KEYS.PERSON_INFORMATION.SOCIAL_SECURITY_NUMBER
      ),
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.PERSON_INFORMATION,
        COMPLEX_FIELD_KEYS.PERSON_INFORMATION.PHONE_NUMBER
      ),
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.PERSON_INFORMATION,
        COMPLEX_FIELD_KEYS.PERSON_INFORMATION.EMAIL_ADDRESS
      ),
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.PERSON_INFORMATION,
        COMPLEX_FIELD_KEYS.PERSON_INFORMATION.HOME_ADDRESS
      ),

      // Government ID Information Complex Fields
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.GOVERNMENT_ID_INFORMATION,
        COMPLEX_FIELD_KEYS.GOVERNMENT_ID_INFORMATION.IS_US_CITIZEN
      ),
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.GOVERNMENT_ID_INFORMATION,
        COMPLEX_FIELD_KEYS.GOVERNMENT_ID_INFORMATION.GOVERNMENT_ID_TYPE
      ),
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.GOVERNMENT_ID_INFORMATION,
        COMPLEX_FIELD_KEYS.GOVERNMENT_ID_INFORMATION.GOVERNMENT_ID_NUMBER
      ),
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.GOVERNMENT_ID_INFORMATION,
        COMPLEX_FIELD_KEYS.GOVERNMENT_ID_INFORMATION.GOVERNMENT_ID_ISSUER_STATE
      ),
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.GOVERNMENT_ID_INFORMATION,
        COMPLEX_FIELD_KEYS.GOVERNMENT_ID_INFORMATION.GOVERNMENT_ID_ISSUER_COUNTRY
      ),
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.GOVERNMENT_ID_INFORMATION,
        COMPLEX_FIELD_KEYS.GOVERNMENT_ID_INFORMATION.GOVERNMENT_ID_EXPIRY
      ),
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.GOVERNMENT_ID_INFORMATION,
        COMPLEX_FIELD_KEYS.GOVERNMENT_ID_INFORMATION.IS_PHYSICAL_ADDRESS_DIFFERENT_THAN_ID_ADDRESS
      ),
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.GOVERNMENT_ID_INFORMATION,
        COMPLEX_FIELD_KEYS.GOVERNMENT_ID_INFORMATION.REASON_PHYSICAL_ADDRESS_IS_DIFFERENT_THAN_ID_ADDRESS
      ),
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.GOVERNMENT_ID_INFORMATION,
        COMPLEX_FIELD_KEYS.GOVERNMENT_ID_INFORMATION
          .OTHER_REASON_PHYSICAL_ADDRESS_IS_DIFFERENT_THAN_ID_ADDRESS
      ),

      // Joint Account Owners & Personal Account Beneficiaries Questions
      FIELD_KEYS.FINANCIAL_APPLICATION.WILL_HAVE_JOINT_ACCOUNT_OWNERS,
      FIELD_KEYS.FINANCIAL_APPLICATION.WILL_HAVE_PERSONAL_ACCOUNT_BENEFICIARIES,

      // Applicant Key Person Role Fields
      FIELD_KEYS.FINANCIAL_APPLICATION.IS_BENEFICIAL_OWNER,
      FIELD_KEYS.FINANCIAL_APPLICATION.IS_AUTHORIZED_SIGNER,
      FIELD_KEYS.FINANCIAL_APPLICATION.IS_CONTROL_PERSON,

      // Employment Information Complex Fields
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.EMPLOYMENT_INFORMATION,
        COMPLEX_FIELD_KEYS.EMPLOYMENT_INFORMATION.EMPLOYMENT_TYPE
      ),
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.EMPLOYMENT_INFORMATION,
        COMPLEX_FIELD_KEYS.EMPLOYMENT_INFORMATION.EMPLOYER
      ),
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.EMPLOYMENT_INFORMATION,
        COMPLEX_FIELD_KEYS.EMPLOYMENT_INFORMATION.JOB_TITLE
      ),
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.EMPLOYMENT_INFORMATION,
        COMPLEX_FIELD_KEYS.EMPLOYMENT_INFORMATION.LENGTH_OF_EMPLOYMENT_YEARS
      ),

      // Personal Account Acceptable Use Complex Fields
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.PERSONAL_ACCOUNT_ACCEPTABLE_USE,
        COMPLEX_FIELD_KEYS.PERSONAL_ACCOUNT_ACCEPTABLE_USE.FOR_BUSINESS
      ),
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.PERSONAL_ACCOUNT_ACCEPTABLE_USE,
        COMPLEX_FIELD_KEYS.PERSONAL_ACCOUNT_ACCEPTABLE_USE.CASH_DEPOSITS
      ),
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.PERSONAL_ACCOUNT_ACCEPTABLE_USE,
        COMPLEX_FIELD_KEYS.PERSONAL_ACCOUNT_ACCEPTABLE_USE.SEND_RECEIVE_WIRES
      ),

      // Organization Information Complex Fields
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.ORGANIZATION_INFORMATION,
        COMPLEX_FIELD_KEYS.ORGANIZATION_INFORMATION.LEGAL_ORGANIZATION_NAME
      ),
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.ORGANIZATION_INFORMATION,
        COMPLEX_FIELD_KEYS.ORGANIZATION_INFORMATION.ORGANIZATION_TYPE
      ),
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.ORGANIZATION_INFORMATION,
        COMPLEX_FIELD_KEYS.ORGANIZATION_INFORMATION.EMPLOYER_IDENTIFICATION_NUMBER
      ),
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.ORGANIZATION_INFORMATION,
        COMPLEX_FIELD_KEYS.ORGANIZATION_INFORMATION.STATE_OF_FORMATION
      ),
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.ORGANIZATION_INFORMATION,
        COMPLEX_FIELD_KEYS.ORGANIZATION_INFORMATION.YEAR_ESTABLISHED
      ),
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.ORGANIZATION_INFORMATION,
        COMPLEX_FIELD_KEYS.ORGANIZATION_INFORMATION.ORGANIZATION_EMAIL_ADDRESS
      ),
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.ORGANIZATION_INFORMATION,
        COMPLEX_FIELD_KEYS.ORGANIZATION_INFORMATION.ORGANIZATION_PHONE_NUMBER
      ),
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.ORGANIZATION_INFORMATION,
        COMPLEX_FIELD_KEYS.ORGANIZATION_INFORMATION.NAICS_CODE
      ),
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.ORGANIZATION_INFORMATION,
        COMPLEX_FIELD_KEYS.ORGANIZATION_INFORMATION
          .ENTITY_OR_TRUST_HAS_25_PERCENT_OR_GREATER_OWNERSHIP_IN_ORGANIZATION
      ),
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.ORGANIZATION_INFORMATION,
        COMPLEX_FIELD_KEYS.ORGANIZATION_INFORMATION.FICO_SBSS_SCORE
      ),

      // Organization Contact Information Complex Fields
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.ORGANIZATION_CONTACT_INFORMATION,
        COMPLEX_FIELD_KEYS.ORGANIZATION_CONTACT_INFORMATION.ORGANIZATION_PHYSICAL_ADDRESS
      ),
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.ORGANIZATION_CONTACT_INFORMATION,
        COMPLEX_FIELD_KEYS.ORGANIZATION_CONTACT_INFORMATION.ORGANIZATION_MAILING_ADDRESS
      ),

      // Organization Account Acceptable Use Complex Fields
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.ORGANIZATION_ACCOUNT_ACCEPTABLE_USE,
        COMPLEX_FIELD_KEYS.ORGANIZATION_ACCOUNT_ACCEPTABLE_USE.SEND_RECEIVE_WIRES
      ),
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.ORGANIZATION_ACCOUNT_ACCEPTABLE_USE,
        COMPLEX_FIELD_KEYS.ORGANIZATION_ACCOUNT_ACCEPTABLE_USE.PROHIBITED_ACTIVITIES
      ),
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.ORGANIZATION_ACCOUNT_ACCEPTABLE_USE,
        COMPLEX_FIELD_KEYS.ORGANIZATION_ACCOUNT_ACCEPTABLE_USE.STORAGE_OF_VALUED_CARDS
      ),
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.ORGANIZATION_ACCOUNT_ACCEPTABLE_USE,
        COMPLEX_FIELD_KEYS.ORGANIZATION_ACCOUNT_ACCEPTABLE_USE.GAMBLING
      ),
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.ORGANIZATION_ACCOUNT_ACCEPTABLE_USE,
        COMPLEX_FIELD_KEYS.ORGANIZATION_ACCOUNT_ACCEPTABLE_USE.LOTTERY_TICKETS
      ),
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.ORGANIZATION_ACCOUNT_ACCEPTABLE_USE,
        COMPLEX_FIELD_KEYS.ORGANIZATION_ACCOUNT_ACCEPTABLE_USE.ADULT_ENTERTAINMENT_FIREARMS
      ),
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.ORGANIZATION_ACCOUNT_ACCEPTABLE_USE,
        COMPLEX_FIELD_KEYS.ORGANIZATION_ACCOUNT_ACCEPTABLE_USE.MARIJUANA_INDUSTRY
      ),
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.ORGANIZATION_ACCOUNT_ACCEPTABLE_USE,
        COMPLEX_FIELD_KEYS.ORGANIZATION_ACCOUNT_ACCEPTABLE_USE.CANNABIS_INVESTMENTS
      ),
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.ORGANIZATION_ACCOUNT_ACCEPTABLE_USE,
        COMPLEX_FIELD_KEYS.ORGANIZATION_ACCOUNT_ACCEPTABLE_USE.ILLEGAL_SUBSTANCES
      ),
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.ORGANIZATION_ACCOUNT_ACCEPTABLE_USE,
        COMPLEX_FIELD_KEYS.ORGANIZATION_ACCOUNT_ACCEPTABLE_USE.CRYPTO_EXCHANGES
      ),
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.ORGANIZATION_ACCOUNT_ACCEPTABLE_USE,
        COMPLEX_FIELD_KEYS.ORGANIZATION_ACCOUNT_ACCEPTABLE_USE.CRYPTO_ACTIVITIES
      ),
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.ORGANIZATION_ACCOUNT_ACCEPTABLE_USE,
        COMPLEX_FIELD_KEYS.ORGANIZATION_ACCOUNT_ACCEPTABLE_USE.VIRTUAL_CURRENCY
      ),
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.ORGANIZATION_ACCOUNT_ACCEPTABLE_USE,
        COMPLEX_FIELD_KEYS.ORGANIZATION_ACCOUNT_ACCEPTABLE_USE.DEFENSE_CONTRACTORS
      ),
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.ORGANIZATION_ACCOUNT_ACCEPTABLE_USE,
        COMPLEX_FIELD_KEYS.ORGANIZATION_ACCOUNT_ACCEPTABLE_USE.PRIVATE_ATMS
      ),
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.ORGANIZATION_ACCOUNT_ACCEPTABLE_USE,
        COMPLEX_FIELD_KEYS.ORGANIZATION_ACCOUNT_ACCEPTABLE_USE.CASHING_CHECKS
      ),
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.ORGANIZATION_ACCOUNT_ACCEPTABLE_USE,
        COMPLEX_FIELD_KEYS.ORGANIZATION_ACCOUNT_ACCEPTABLE_USE.INITIAL_CASH_DEPOSIT
      ),

      // Digital Banking Information Complex Fields
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.DIGITAL_BANKING_INFORMATION,
        COMPLEX_FIELD_KEYS.DIGITAL_BANKING_INFORMATION.DIGITAL_BANKING_USERNAME
      ),
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.DIGITAL_BANKING_INFORMATION,
        COMPLEX_FIELD_KEYS.DIGITAL_BANKING_INFORMATION.DIGITAL_BANKING_EMAIL_ADDRESS
      ),
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.DIGITAL_BANKING_INFORMATION,
        COMPLEX_FIELD_KEYS.DIGITAL_BANKING_INFORMATION.DIGITAL_BANKING_PHONE_NUMBER
      ),
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.DIGITAL_BANKING_INFORMATION,
        COMPLEX_FIELD_KEYS.DIGITAL_BANKING_INFORMATION.DIGITAL_BANKING_ROLE
      ),

      // Security Questions Information Complex Fields
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.SECURITY_QUESTIONS_INFORMATION,
        COMPLEX_FIELD_KEYS.SECURITY_QUESTIONS_INFORMATION.SECURITY_QUESTION_1
      ),
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.SECURITY_QUESTIONS_INFORMATION,
        COMPLEX_FIELD_KEYS.SECURITY_QUESTIONS_INFORMATION.SECURITY_QUESTION_2
      ),
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.SECURITY_QUESTIONS_INFORMATION,
        COMPLEX_FIELD_KEYS.SECURITY_QUESTIONS_INFORMATION.SECURITY_QUESTION_3
      ),
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.SECURITY_QUESTIONS_INFORMATION,
        COMPLEX_FIELD_KEYS.SECURITY_QUESTIONS_INFORMATION.SECURITY_ANSWER_1
      ),
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.SECURITY_QUESTIONS_INFORMATION,
        COMPLEX_FIELD_KEYS.SECURITY_QUESTIONS_INFORMATION.SECURITY_ANSWER_2
      ),
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.SECURITY_QUESTIONS_INFORMATION,
        COMPLEX_FIELD_KEYS.SECURITY_QUESTIONS_INFORMATION.SECURITY_ANSWER_3
      ),

      // Debit Card Information Complex Fields
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.DEBIT_CARD_INFORMATION,
        COMPLEX_FIELD_KEYS.DEBIT_CARD_INFORMATION.DEBIT_CARD_NAME_DISPLAYED
      ),
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.DEBIT_CARD_INFORMATION,
        COMPLEX_FIELD_KEYS.DEBIT_CARD_INFORMATION.BUSINESS_NAME_DISPLAYED_ON_CARD
      ),
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.DEBIT_CARD_INFORMATION,
        COMPLEX_FIELD_KEYS.DEBIT_CARD_INFORMATION.USE_HOME_ADDRESS_FOR_DEBIT_CARD_MAILING_ADDRESS
      ),
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.DEBIT_CARD_INFORMATION,
        COMPLEX_FIELD_KEYS.DEBIT_CARD_INFORMATION.DEBIT_CARD_MAILING_ADDRESS
      ),
    ];

    const bankerRequiredFields: string[] = [
      ...applicantRequiredFields,

      // Employment Information Complex Fields
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.EMPLOYMENT_INFORMATION,
        COMPLEX_FIELD_KEYS.EMPLOYMENT_INFORMATION.ATTORNEY_BAR_NUMBER
      ),

      // Personal Risk Review Complex Fields
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.PERSONAL_ACCOUNT_RISK_REVIEW,
        COMPLEX_FIELD_KEYS.PERSONAL_ACCOUNT_RISK_REVIEW.LOCATION
      ),
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.PERSONAL_ACCOUNT_RISK_REVIEW,
        COMPLEX_FIELD_KEYS.PERSONAL_ACCOUNT_RISK_REVIEW.NSF_HISTORY
      ),
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.PERSONAL_ACCOUNT_RISK_REVIEW,
        COMPLEX_FIELD_KEYS.PERSONAL_ACCOUNT_RISK_REVIEW.NEGATIVE_SOCIAL_MEDIA_CHECK
      ),
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.PERSONAL_ACCOUNT_RISK_REVIEW,
        COMPLEX_FIELD_KEYS.PERSONAL_ACCOUNT_RISK_REVIEW.ADDRESS_VERIFICATION
      ),

      // Law Firm Risk Review Complex Fields
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.LAW_FIRM_ORGANIZATION_ACCOUNT_RISK_REVIEW,
        COMPLEX_FIELD_KEYS.LAW_FIRM_ORGANIZATION_ACCOUNT_RISK_REVIEW.ACCEPTABLE_BUSINESS
      ),
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.LAW_FIRM_ORGANIZATION_ACCOUNT_RISK_REVIEW,
        COMPLEX_FIELD_KEYS.LAW_FIRM_ORGANIZATION_ACCOUNT_RISK_REVIEW.LOCATION
      ),
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.LAW_FIRM_ORGANIZATION_ACCOUNT_RISK_REVIEW,
        COMPLEX_FIELD_KEYS.LAW_FIRM_ORGANIZATION_ACCOUNT_RISK_REVIEW.BANKRUPTCY
      ),
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.LAW_FIRM_ORGANIZATION_ACCOUNT_RISK_REVIEW,
        COMPLEX_FIELD_KEYS.LAW_FIRM_ORGANIZATION_ACCOUNT_RISK_REVIEW.BALANCE_EVER_BELOW_ZERO
      ),
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.LAW_FIRM_ORGANIZATION_ACCOUNT_RISK_REVIEW,
        COMPLEX_FIELD_KEYS.LAW_FIRM_ORGANIZATION_ACCOUNT_RISK_REVIEW.BAR_DISCIPLINARY_ACTION
      ),
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.LAW_FIRM_ORGANIZATION_ACCOUNT_RISK_REVIEW,
        COMPLEX_FIELD_KEYS.LAW_FIRM_ORGANIZATION_ACCOUNT_RISK_REVIEW.TIMES_ACCOUNT_OVERDRAWN
      ),
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.LAW_FIRM_ORGANIZATION_ACCOUNT_RISK_REVIEW,
        COMPLEX_FIELD_KEYS.LAW_FIRM_ORGANIZATION_ACCOUNT_RISK_REVIEW.NEGATIVE_SOCIAL_MEDIA_CHECK
      ),

      // Non-law firm Risk Review Complex Fields
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.NON_LAW_FIRM_ORGANIZATION_ACCOUNT_RISK_REVIEW,
        COMPLEX_FIELD_KEYS.NON_LAW_FIRM_ORGANIZATION_ACCOUNT_RISK_REVIEW.ACCEPTABLE_BUSINESS
      ),
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.NON_LAW_FIRM_ORGANIZATION_ACCOUNT_RISK_REVIEW,
        COMPLEX_FIELD_KEYS.NON_LAW_FIRM_ORGANIZATION_ACCOUNT_RISK_REVIEW.LOCATION
      ),
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.NON_LAW_FIRM_ORGANIZATION_ACCOUNT_RISK_REVIEW,
        COMPLEX_FIELD_KEYS.NON_LAW_FIRM_ORGANIZATION_ACCOUNT_RISK_REVIEW.NSF_HISTORY
      ),
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.NON_LAW_FIRM_ORGANIZATION_ACCOUNT_RISK_REVIEW,
        COMPLEX_FIELD_KEYS.NON_LAW_FIRM_ORGANIZATION_ACCOUNT_RISK_REVIEW.YEARS_IN_INDUSTRY
      ),
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.NON_LAW_FIRM_ORGANIZATION_ACCOUNT_RISK_REVIEW,
        COMPLEX_FIELD_KEYS.NON_LAW_FIRM_ORGANIZATION_ACCOUNT_RISK_REVIEW.IN_GOOD_STANDING_SECRETARY_OF_STATE
      ),
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.NON_LAW_FIRM_ORGANIZATION_ACCOUNT_RISK_REVIEW,
        COMPLEX_FIELD_KEYS.NON_LAW_FIRM_ORGANIZATION_ACCOUNT_RISK_REVIEW.NEGATIVE_SOCIAL_MEDIA_CHECK
      ),
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.NON_LAW_FIRM_ORGANIZATION_ACCOUNT_RISK_REVIEW,
        COMPLEX_FIELD_KEYS.NON_LAW_FIRM_ORGANIZATION_ACCOUNT_RISK_REVIEW.ADDRESS_VERIFICATION
      ),
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.NON_LAW_FIRM_ORGANIZATION_ACCOUNT_RISK_REVIEW,
        COMPLEX_FIELD_KEYS.NON_LAW_FIRM_ORGANIZATION_ACCOUNT_RISK_REVIEW.BENEFICIAL_OWNER_INFORMATION_COMPLETE
      ),
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.NON_LAW_FIRM_ORGANIZATION_ACCOUNT_RISK_REVIEW,
        COMPLEX_FIELD_KEYS.NON_LAW_FIRM_ORGANIZATION_ACCOUNT_RISK_REVIEW.BENEFICIAL_OWNER_1_CREDIT_SCORE
      ),
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.NON_LAW_FIRM_ORGANIZATION_ACCOUNT_RISK_REVIEW,
        COMPLEX_FIELD_KEYS.NON_LAW_FIRM_ORGANIZATION_ACCOUNT_RISK_REVIEW.BENEFICIAL_OWNER_2_CREDIT_SCORE
      ),
      
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.NON_LAW_FIRM_ORGANIZATION_ACCOUNT_RISK_REVIEW,
        COMPLEX_FIELD_KEYS.NON_LAW_FIRM_ORGANIZATION_ACCOUNT_RISK_REVIEW.BENEFICIAL_OWNER_3_CREDIT_SCORE
      ),
      buildComplexFieldKey(
        FIELD_KEYS.FINANCIAL_APPLICATION.NON_LAW_FIRM_ORGANIZATION_ACCOUNT_RISK_REVIEW,
        COMPLEX_FIELD_KEYS.NON_LAW_FIRM_ORGANIZATION_ACCOUNT_RISK_REVIEW.BENEFICIAL_OWNER_4_CREDIT_SCORE
      ),
    ];

    const requiredFields =
      executionContextType === FinancialApplicationFormSectionContextType.Banker
        ? [...bankerRequiredFields]
        : executionContextType === FinancialApplicationFormSectionContextType.Applicant
          ? [...applicantRequiredFields]
          : [];

    const fields = [
      ...schemaFactory.buildModelSchema<FinancialApplication>(
        MODEL_KEYS.FINANCIAL_APPLICATION,
        onValueChange,
        form,
        errors,
        requiredFields,
        {
          PersonInformationExpressionSchema: PersonInformationComplexField,
          EmploymentInformationExpressionSchema: EmploymentInformationComplexField,
          DigitalBankingInformationExpressionSchema: DigitalBankingInformationComplexField,
          DebitCardInformationExpressionSchema: DebitCardInformationComplexField,
          GovernmentIdInformationExpressionSchema: GovernmentIdInformationComplexField,
          SecurityQuestionsInformationExpressionSchema: SecurityQuestionsInformationComplexField,
          OrganizationInformationExpressionSchema: OrganizationInformationComplexField,
          PersonalAccountAcceptableUseExpressionSchema: PersonalAccountAcceptableUseComplexField,
          OrganizationAccountAcceptableUseExpressionSchema:
            OrganizationAccountAcceptableUseComplexField,
          PersonalAccountRiskReviewExpressionSchema: PersonalAccountRiskReviewComplexField,
          OrganizationContactInformationExpressionSchema:
            OrganizationContactInformationComplexField,
          LawFirmOrganizationAccountRiskReviewExpressionSchema:
            LawFirmOrganizationAccountRiskReviewComplexField,
          NonLawFirmOrganizationAccountRiskReviewExpressionSchema:
            NonLawFirmOrganizationAccountRiskReviewComplexField,
          FinancialApplicationDocumentsExpressionSchema: FinancialApplicationDocumentsComplexField,
        }
      ),
    ];

    return fields;
  };
};
